import branch, { BranchError, DeepLinkData } from "branch-sdk";
import { useMediaQuery } from "hooks/useMediaQuery";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { store } from "redux_store/configureReduxStore";

import config from "config";
import {
  AnalyticsConst,
  AnalyticsInstance,
  BranchConst,
  OrderModules,
  OrderReduxAction,
  OrderReduxModels,
  RootState,
  StoreModules,
  StoreReduxAction,
} from "gyg_common";
import useCreateUpdateOrder from "gyg_common/hooks/useCreateUpdateOrder";
import { Screens } from "navigation/const";
import { OrderSetup } from "views/components/OrderSetup/OrderSetup";

export interface OrderSetupContainerProps {
  isVisible: boolean;
  onCloseModal: () => void;
  onConfirmClick: () => void;
}

export const OrderSetupContainer: React.FC<OrderSetupContainerProps> = ({
  isVisible,
  onCloseModal,
  onConfirmClick,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const { isDesktopScreen } = useMediaQuery();

  const { selectedStore, storeOpenTime, storeOrderOffset, storeOrderTimes } =
    useSelector((state: RootState) => state.store);
  const { totalPrice: cartTotalPrice } = useSelector(
    (state: RootState) => state.cart
  );
  const {
    menuStructure,
    loading: menuLoading,
    error: menuError,
    menuOpenTime,
  } = useSelector((state: RootState) => state.menu);
  const { orderCollectionType, orderASAP, orderTime, getOrderResponse } =
    useSelector((state: RootState) => state.order);

  const { isBrazeInitialised } = useSelector((s: RootState) => s.user);
  const shouldCreateOrUpdateOrder = useRef(false);
  const createOrUpdateOrder = useCreateUpdateOrder(store, pathname, false);

  /** Reset the user's order setup confirmation when they click change restaruants.
   * Then navigate them to the restaurant's list.
   */
  const handleChangeRestaurant = () => {
    onCloseModal();
    history.push(Screens.Restaurants, {
      previousScreen: pathname,
    });
  };

  /**
   * Set the user's order setup
   * Set the user's confirmation and load the menu for the selected store
   * Then navigate to the appropriate screen
   */
  const handleOrderConfirmation = (
    orderSetup: OrderReduxModels.SetupOrderProps
  ) => {
    let selectedTime = orderSetup.orderTime;
    if (
      selectedTime &&
      OrderModules.OrderUtils.isOrderTimeAsapExpired(selectedTime, true)
    ) {
      const calculatedAsapTime =
        StoreModules.StoreUtils.calculateAsapTimeFromOffset(
          cartTotalPrice,
          storeOrderOffset
        ).valueOf();

      const time = new Date(
        OrderModules.OrderUtils.getNextAvailableTime(
          store,
          calculatedAsapTime,
          storeOrderTimes,
          selectedStore?.timeZoneInfo.storeTimeZone
        )
      );
      selectedTime = time.valueOf();
    }
    shouldCreateOrUpdateOrder.current = true;
    dispatch(
      OrderReduxAction.setupOrder({
        orderASAP: orderSetup.orderASAP,
        orderTime: selectedTime,
        orderCollectionType: orderSetup.orderCollectionType,
      })
    );
    dispatch(OrderReduxAction.confirmOrderSetup(true));

    onConfirmClick();
  };

  const handleNavigateToStoreDetails = () => {
    onCloseModal();
    dispatch(StoreReduxAction.setStoreInfo(selectedStore));
    history.push(Screens.Restaurants, {
      previousScreen: pathname,
    });
  };

  const handleOrderChange = (
    orderSetup: OrderReduxModels.OrderCollectionTypeProps
  ) => {
    dispatch(
      OrderReduxAction.setupOrderCollectionType({
        orderCollectionType: orderSetup.orderCollectionType,
      })
    );
  };

  const handleDownloadLink = (platform: string) => {
    AnalyticsInstance.trackEvent(
      AnalyticsConst.Events.TableServiceOrderSetup,
      {}
    );

    if (isDesktopScreen) {
      window.open(
        platform === "ios"
          ? `${config.appStoreLink}`
          : `${config.googlePlayLink}`,
        "_blank"
      );
    } else {
      const linkData: DeepLinkData = {
        data: {
          path: BranchConst.BranchPaths.OrderSetup,
          pickUpType: "Table service",
        },
      };
      try {
        branch.link(linkData, function (err: BranchError, link: string | null) {
          if (link) {
            window.open(link, "_blank");
          }
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onCloseIconClick = () => {
    onCloseModal();
  };

  useEffect(() => {
    if (orderTime && shouldCreateOrUpdateOrder.current) {
      shouldCreateOrUpdateOrder.current = false;
      createOrUpdateOrder(getOrderResponse?.orderId);
    }
  }, [createOrUpdateOrder, getOrderResponse?.orderId, orderTime]);

  /**
   * Analytics view
   */
  useEffect(() => {
    if (isVisible && isBrazeInitialised) {
      AnalyticsInstance.trackView({
        page_name: "order_setup",
        page_type: "order_setup_view",
      });
    }
  }, [isVisible, isBrazeInitialised]);

  useEffect(() => {
    if (selectedStore) {
      dispatch(
        StoreReduxAction.setStoreOpenTime(
          StoreModules.StoreUtils.checkStoreOpenHours(
            selectedStore.tradingHours,
            selectedStore.timeZoneInfo.storeTimeZone
          )
        )
      );
    }
  }, [dispatch, selectedStore]);

  return (
    <OrderSetup
      cartTotalPrice={cartTotalPrice}
      storeOrderOffset={storeOrderOffset}
      menuError={menuError}
      menuLoading={menuLoading}
      menuStructure={menuStructure}
      storeOpenTime={storeOpenTime}
      storeOrderTimes={storeOrderTimes}
      menuOpenTime={menuOpenTime}
      isVisible={isVisible}
      selectedStore={selectedStore}
      orderCollectionType={orderCollectionType}
      orderASAP={orderASAP}
      orderTime={orderTime}
      onOrderConfirmClick={handleOrderConfirmation}
      onChangeRestaurantClick={handleChangeRestaurant}
      onChangeOrderCollectionType={handleOrderChange}
      navigateBack={() => history.goBack()}
      navigateToStoreDetails={handleNavigateToStoreDetails}
      onCloseModal={onCloseIconClick}
      navigateToDownload={handleDownloadLink}
    />
  );
};
